/**
 * TNK-132 — shared tab strip across the three Profitability tools:
 * ROI Calculator, Break-Even Calculator, and Profit Analytics. The
 * three pages used to live as separate tiles on /tools but they
 * cover overlapping ground (per-sale projection / lot-level cover
 * point / aggregate dashboards), so the audit fold consolidates
 * them under a single "Profitability" navigation entry — same three
 * routes, one virtual home.
 *
 * Drop this strip at the top of each member page's section and pass
 * the matching `current` key. The strip handles the link styling +
 * keyboard accessibility; nothing else.
 */
import { Link } from "@tanstack/react-router";
import { BarChart2, Crosshair, Percent } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type ProfitabilityTab = "roi" | "breakeven" | "analytics";

interface TabDef {
  key: ProfitabilityTab;
  label: string;
  to: string;
  Icon: LucideIcon;
}

const TABS: TabDef[] = [
  { key: "roi", label: "ROI", to: "/tools/roi", Icon: Percent },
  {
    key: "breakeven",
    label: "Break-Even",
    to: "/tools/breakeven",
    Icon: Crosshair,
  },
  {
    key: "analytics",
    label: "Analytics",
    to: "/tools/analytics",
    Icon: BarChart2,
  },
];

export function ProfitabilityTabs({ current }: { current: ProfitabilityTab }) {
  return (
    <nav
      aria-label="Profitability"
      className="-mx-1 flex shrink-0 items-center gap-1 overflow-x-auto pb-3"
    >
      {TABS.map(({ key, label, to, Icon }) => {
        const active = key === current;
        return (
          <Link
            key={key}
            to={to}
            className={
              active
                ? "inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary"
                : "inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:bg-card/80 hover:text-foreground"
            }
          >
            <Icon size={13} />
            {label}
          </Link>
        );
      })}
    </nav>
  );
}
